import { BiTime, BiUser } from "react-icons/bi";
import React from "react";
import styles from "./index.module.scss";
import { TIME_SLOTS } from "constants/variables";
import { getDuration } from "components/FiltersSidebar/helpers";
import { getHourLengthFromMilliseconds } from "utilities";

// Returns the vivid (full-opacity) version of a solid HSL/hex color for the border accent
const vividColor = (color) => {
  if (!color || color.includes('gradient')) return null;
  const match = color.match(/hsla?\(\s*([^,]+),\s*([^,]+),\s*([^,]+)(?:,\s*[^)]*)?\)/);
  if (match) return `hsl(${match[1]}, ${match[2]}, ${match[3]})`;
  if (color.startsWith('#')) return color.slice(0, 7);
  return color;
};

const getClassName = (length, className, slotDuration, studiosLength) => {
  const stylesArray = [];

  if (slotDuration === TIME_SLOTS.MINUTES_15) {
    if (length >= 2) {
      stylesArray.push(styles[`${className}_line-clamp`]);
    } else if (length < 0.5 && studiosLength > 1) {
      stylesArray.push(styles[`${className}_extra-small-duration`]);
    } else if (length === 0.5) {
      if (studiosLength > 5) {
        stylesArray.push(styles[`${className}_ex-small-duration`]);
      } else {
        stylesArray.push(styles[`${className}_extra-small-duration`]);
      }
    } else if (length > 0.5 && length <= 0.75) {
      stylesArray.push(styles[`${className}_small-duration`]);
    } else if (length > 0.75 && length <= 1) {
      stylesArray.push(styles[`${className}_medium-duration`]);
    }
  } else {
    if (length > 0.5) {
      stylesArray.push(styles[`${className}_uncut`]);
    }
  }
  if (studiosLength === 7) {
    stylesArray.push(styles["class-card_standard"]);
  }
  if (studiosLength > 9) {
    if (length <= 0.5) {
      stylesArray.push(styles["class-card_small"]);
    }
    if (length < 0.5) {
      stylesArray.push(styles[`${className}_min-duration`]);
    }
  }
  if (studiosLength > 11) {
    stylesArray.push(styles[`${className}_extra-small`]);
  } else if (studiosLength > 6) {
    stylesArray.push(styles[`${className}_small`]);
  } else if (studiosLength < 4) {
    stylesArray.push(styles[`${className}_large`]);
    if (length <= 0.5) {
      stylesArray.push(styles["class-card_small"]);
    }
  } else {
    stylesArray.push(styles[`${className}_medium`]);
    if (length <= 0.5) {
      stylesArray.push(styles["class-card_small"]);
    }
  }

  return stylesArray.join(" ");
};

// 20 minutes expressed in hours — cards at or under this threshold get compact rendering
const COMPACT_THRESHOLD = 20 / 60;

// Tooltip ID — one shared tooltip element in the DOM
const TOOLTIP_ID = 'ss-card-tooltip';

const showTooltip = (text, rect) => {
  let tip = document.getElementById(TOOLTIP_ID);
  if (!tip) {
    tip = document.createElement('div');
    tip.id = TOOLTIP_ID;
    tip.style.cssText = [
      'position:fixed',
      'background:#1a1a2e',
      'color:#fff',
      'padding:4px 10px',
      'border-radius:5px',
      'font-size:12px',
      'font-weight:600',
      'white-space:nowrap',
      'z-index:99999',
      'pointer-events:none',
      'box-shadow:0 2px 8px rgba(0,0,0,0.25)',
      'line-height:1.5',
      'max-width:280px',
      'overflow:hidden',
      'text-overflow:ellipsis',
    ].join(';');
    document.body.appendChild(tip);
  }
  tip.textContent = text;
  tip.style.left = rect.left + 'px';
  tip.style.top = Math.max(8, rect.top - 36) + 'px';
  tip.style.display = 'block';
};

const hideTooltip = () => {
  const tip = document.getElementById(TOOLTIP_ID);
  if (tip) tip.style.display = 'none';
};

const ClassCard = (eventInfo) => {
  const { event, timeText } = eventInfo;
  const slotDuration = getDuration(
    getHourLengthFromMilliseconds(
      eventInfo.view.calendar.currentData.options.slotDuration.milliseconds
    )
  );
  const daysCount = eventInfo.event.extendedProps?.weekDatesCount
    ?? eventInfo.view.calendar.currentData.options.duration?.days
    ?? 1;
  const studiosLength =
    eventInfo.view.calendar.currentData.resourceSource.meta.length * daysCount;
  const hours = new Date(event.start).getHours();
  const minutes = new Date(event.start).getMinutes() / 60;
  const text = eventInfo.event.extendedProps.className || event.title;
  const length = eventInfo.event?.extendedProps.length;
  const background = eventInfo.event.extendedProps.background;
  const teachers = event.extendedProps.teachers;
  const hasTeacher = teachers && teachers.length > 0 && teachers.some(t => t && t.trim() !== '');

  const isCompact = length <= COMPACT_THRESHOLD;

  // Overlay approach: 28% opacity color over white gives a richer tint while keeping
  // text readable. Works for both solid and gradient backgrounds.
  const cardBg = background || '#D9D9D9';
  const borderAccent = vividColor(cardBg);
  const isGradient = cardBg.includes('gradient');

  const classNameCard = [
    styles.card,
    getClassName(length, "card", slotDuration, studiosLength),
  ].join(" ");
  const classNameCardItem = [
    styles["card__item"],
    getClassName(length, "card__item", slotDuration, studiosLength),
  ].join(" ");
  const classNameTitle = [
    styles["card__item"],
    styles["card__item_title"],
    getClassName(length, "card__item", slotDuration, studiosLength),
  ].join(" ");

  const textColor = '#1a1a1a';
  const subTextColor = '#444';
  const timeColor = '#555';
  const iconColor = '#888';

  return (
    <div
      className={["test", styles.cardWrapper].join(" ")}
      data-length={length}
      style={{
        height: "100%",
        position: "relative",
        background: "white",
        borderLeft: isGradient ? "none" : (borderAccent ? `3px solid ${borderAccent}` : "none"),
        borderRadius: "2px",
        overflow: "hidden",
      }}
      data-time={hours + minutes}
      onMouseEnter={(e) => {
        const nameEl = e.currentTarget.querySelector('[data-id="card-name"]');
        if (nameEl && (nameEl.scrollWidth > nameEl.offsetWidth || nameEl.scrollHeight > nameEl.offsetHeight)) {
          showTooltip(text, e.currentTarget.getBoundingClientRect());
        }
      }}
      onMouseLeave={hideTooltip}
    >
      {/* Gradient accent strip — visible left bar for multi-level (gradient) cards */}
      {isGradient && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "3px",
            background: cardBg,
            opacity: 1,
            pointerEvents: "none",
            zIndex: 2,
            borderRadius: "2px 0 0 2px",
          }}
        />
      )}
      {/* Tinted color overlay at 28% opacity over white background */}
      {(
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: cardBg,
            opacity: 0.38,
            pointerEvents: "none",
          }}
        />
      )}
      <div
        className={classNameCard}
        style={{
          background: "transparent",
          position: "relative",
          zIndex: 1,
          padding: isCompact ? "0 2px" : "1px",
          paddingLeft: isGradient ? "5px" : undefined,
        }}
      >
        {isCompact ? (
          <div
            data-id="card-name"
            style={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              fontWeight: 700,
              fontSize: "11px",
              color: textColor,
              lineHeight: 1.2,
            }}
          >
            {text}
          </div>
        ) : (
          <>
            <div className={classNameTitle} data-id="card-name">
              <span style={{ fontWeight: 700, color: textColor }}>{text}</span>
            </div>
            <div className={classNameCardItem} data-id="card-item">
              <BiUser
                className="me-1"
                width={13}
                size={18}
                color={iconColor}
              />
              {hasTeacher ? (
                <span style={{ fontWeight: 400, color: subTextColor }}>{teachers.join(", ")}</span>
              ) : (
                <i style={{ color: '#aaa', fontSize: "0.9em" }}>+ Add teacher</i>
              )}
            </div>
            <div
              className={[
                classNameCardItem,
                styles.time,
                styles["card__item_uncut"],
              ].join(" ")}
            >
              <BiTime
                className="me-1"
                size={18}
                color={iconColor}
              />
              <span style={{ fontWeight: 400, color: timeColor }}>{timeText || ""}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClassCard;

